import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Building2, MapPin, Phone, Mail, Globe, Package, Loader2, 
  AlertTriangle, CheckCircle2, Star, ExternalLink
} from 'lucide-react';
import { getFirstImage } from '@/utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SharedCatalogPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchSharedCatalog();
  }, [token]);

  const fetchSharedCatalog = async () => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/shared/${token}`);
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al cargar el catálogo');
      }

      const catalogData = await res.json();
      setData(catalogData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600">Cargando catálogo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Link no válido</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button asChild variant="outline">
              <Link to="/">Ir al inicio</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, products, link_label } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white py-3 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className="bg-purple-600 text-white">Catálogo compartido</Badge>
            {link_label && <span className="text-slate-300 text-sm">• {link_label}</span>}
          </div>
          <a 
            href="/" 
            className="text-sm text-slate-300 hover:text-white flex items-center gap-1"
          >
            <ExternalLink className="w-4 h-4" />
            TexHub
          </a>
        </div>
      </div>

      {/* Company Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-start gap-6">
            {company.logo_url ? (
              <img 
                src={company.logo_url} 
                alt={company.company_name}
                className="w-24 h-24 rounded-xl object-cover border-2 border-slate-200"
              />
            ) : (
              <div className="w-24 h-24 bg-slate-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-12 h-12 text-slate-400" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-slate-900">{company.company_name}</h1>
                {company.verified && (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Verificado
                  </Badge>
                )}
                {company.is_sponsored && (
                  <Badge className="bg-yellow-100 text-yellow-700">
                    <Star className="w-3 h-3 mr-1" />
                    {company.sponsor_tier}
                  </Badge>
                )}
              </div>
              <p className="text-slate-600 mb-3">{company.description}</p>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                {company.location_city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {company.location_city}, {company.location_state}
                  </span>
                )}
                {company.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {company.phone}
                  </span>
                )}
                {company.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {company.email}
                  </span>
                )}
                {company.website && (
                  <a 
                    href={company.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <Globe className="w-4 h-4" />
                    Sitio web
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5" />
            Catálogo de Productos
          </h2>
          <Badge variant="outline">{products.length} productos</Badge>
        </div>

        {products.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Este catálogo no tiene productos publicados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((product) => (
              <Card key={product.product_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-square bg-slate-100">
                  {product.images && product.images.length > 0 ? (
                    <img 
                      src={getFirstImage(product.images)} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-1 line-clamp-2">{product.name}</h3>
                  <p className="text-sm text-slate-500 mb-2">{product.category}</p>
                  {product.price_per_unit && (
                    <p className="font-bold text-lg text-emerald-600">
                      ${product.price_per_unit.toLocaleString()} MXN
                      <span className="text-xs font-normal text-slate-500">/{product.unit || 'unidad'}</span>
                    </p>
                  )}
                  {product.minimum_order && (
                    <p className="text-xs text-slate-500 mt-1">
                      Pedido mínimo: {product.minimum_order} {product.unit || 'unidades'}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-slate-100 border-t py-6 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-500">
            Catálogo compartido vía <a href="/" className="text-emerald-600 hover:underline">TexHub</a> • 
            Plataforma B2B para la industria textil en México
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharedCatalogPage;
